import { useState } from 'react'

import { Box, BoxList, Button, Skeleton, Text } from '@factorialco/deprecated-design-system'
import {
  ArchiveWorkingConditionGroupDocument,
  ContractsSettingPageDocument,
  WorkingConditionsListFragment,
} from 'generated/resources/graphql'

import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useMutation } from 'modules/core/hooks/useMutation'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import ArchiveWorkingConditionModal from 'modules/contracts_settings/components/WorkConditions/ArchiveWorkingConditionModal'

interface Props {
  groups: WorkingConditionsListFragment[]
  loading?: boolean
  error?: unknown
}

interface PopoverProps {
  onArchive: () => void
}

const Empty = () => (
  <BoxList>
    <BoxList.Item>
      <Text.Normal color="grey700" size="200" weight="normal">
        {i18n.t('settings.working_condititions.empty_list')}
      </Text.Normal>
    </BoxList.Item>
  </BoxList>
)

const ConditionsSkeleton = () => (
  <BoxList>
    {[...Array(3)].map((_, index) => (
      <BoxList.Item key={index}>
        <Box width="full" gap="s16">
          <Skeleton width="s360" height="s16" />
          <Skeleton width="s300" height="s12" />
        </Box>
      </BoxList.Item>
    ))}
  </BoxList>
)

const Popover = ({ onArchive }: PopoverProps) => (
  <PopoverMenu type="list">
    <PopoverMenu.Trigger>
      <Button.Decorative type="secondary" iconName="ellipsis" />
    </PopoverMenu.Trigger>
    <PopoverMenu.Content side="bottom" align="center">
      <PopoverMenu.TouchableOption
        title={i18n.t('settings.working_condititions.popover.archive')}
        onClick={onArchive}
        danger
      />
    </PopoverMenu.Content>
  </PopoverMenu>
)

const WorkConditionsList = ({ loading, groups }: Props) => {
  const { navigation } = useNavigation()
  const [workingConditionIdToArchieve, setWorkingConditionIdToArchieve] = useState<string>()

  const [archiveWorkingCondition] = useMutation(ArchiveWorkingConditionGroupDocument, {
    extractorFn: (data) => data.workingConditionsMutations.archiveWorkingconditionsGroup,
    refetchQueries: [ContractsSettingPageDocument],
  })

  if (loading) {
    return <ConditionsSkeleton />
  }

  if (groups.length === 0) {
    return <Empty />
  }

  const onArchive = async (id: string) => {
    const result = await archiveWorkingCondition({ variables: { groupId: id } })
    const handled = handleResult(result, () => {})

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t(
          'company_settings.general.employment_data.work_conditions.messages.archive_success'
        ),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('errors.generic_error'),
      })
    }

    setWorkingConditionIdToArchieve(undefined)
  }

  return (
    <BoxList>
      {groups.map((group) => (
        <BoxList.Item key={group.id}>
          <Box flexDirection="row" width="full" justifyContent="spaceBetween">
            <Box minWidth="none" width="s240" gap="s2" flexGrow>
              <Text.Normal size="300" weight="medium" color="grey1000">
                {group.name}
              </Text.Normal>
              <Text.Normal size="200" color="grey800">
                {group.description}
              </Text.Normal>
            </Box>
            <Box gap="s8" flexDirection="row" alignItems="center" justifyContent="flexEnd">
              <Popover onArchive={() => setWorkingConditionIdToArchieve(group.groupId)} />
              <Button.Router
                aria-label={i18n.t(
                  'company_settings.general.employment_data.work_conditions.common.navigation.details'
                )}
                to={navigation.settings.contracts_settings.workingConditions.version.fullPath({
                  identifier: group.id,
                })}
                type="secondary"
                iconName="arrow-right"
              />
            </Box>
          </Box>
          {workingConditionIdToArchieve && (
            <ArchiveWorkingConditionModal
              onClose={() => setWorkingConditionIdToArchieve(undefined)}
              onSubmit={() => onArchive(workingConditionIdToArchieve)}
            />
          )}
        </BoxList.Item>
      ))}
    </BoxList>
  )
}

export default WorkConditionsList
